import { Link } from 'react-router-dom';
import PublicNav from '../../../components/layout/PublicNav';

/**
 * Mission Component
 * 🎯 Describes MnyMny's mission and values
 */
const Mission = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#6C5DD3] to-[#8247E5]">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-white bg-opacity-95 backdrop-blur-sm rounded-3xl shadow-xl overflow-hidden">
          <div className="p-8 md:p-12">
            <PublicNav />

            <h1 className="text-3xl font-bold text-[#6C5DD3] mb-6">Our Mission</h1>
            
            <div className="prose max-w-none">
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-xl mb-10">
                <p className="text-xl font-medium text-gray-800 italic">
                  "To empower everyone to take control of their finances through intelligent AI insights, 
                  comprehensive financial management, and personalized guidance for building wealth and 
                  achieving financial goals."
                </p>
              </div>

              <p className="text-gray-700 text-lg mb-6">
                At MnyMny, we believe that financial wellness is achievable for everyone, whether you're 
                managing money solo or with a partner. Our mission is to transform how people manage their 
                finances by providing a complete platform that brings together income sources, accounts, 
                budgets, expenses, assets, and transactions—all enhanced with AI-powered insights that help 
                you make smarter financial decisions, pay off debt, build savings, and plan for the future.
              </p>
              <p className="text-gray-700 text-lg mb-6">
                We're designed for individuals first, with powerful optional partner features for those who 
                want to manage finances collaboratively. Our machine learning capabilities continuously learn 
                from your financial patterns to provide personalized recommendations for debt management, 
                emergency fund building, retirement planning, and more.
              </p>

              <h2 className="text-2xl font-semibold text-gray-800 mt-10 mb-4">Our Core Values</h2>
              
              <div className="space-y-6 mb-10">
                <div className="flex">
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-[#6C5DD3] text-white mr-4">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"></path>
                      <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-gray-800 mb-1">Intelligence & Innovation</h3>
                    <p className="text-gray-700">
                      We leverage AI and machine learning to provide personalized financial insights, 
                      helping you make smarter decisions about debt, savings, budgeting, and retirement planning.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-[#6C5DD3] text-white mr-4">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-gray-800 mb-1">Trust & Security</h3>
                    <p className="text-gray-700">
                      We prioritize the security of your financial data with industry-standard encryption 
                      and privacy controls. Your data is yours, and you control who sees what.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-[#6C5DD3] text-white mr-4">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-gray-800 mb-1">Partnership (Optional)</h3>
                    <p className="text-gray-700">
                      While designed for individuals, we offer powerful partner features for those who want 
                      to manage finances collaboratively. Share accounts, budgets, and goals while maintaining 
                      full control over your privacy.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="flex-shrink-0 h-12 w-12 flex items-center justify-center rounded-full bg-[#6C5DD3] text-white mr-4">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-gray-800 mb-1">Financial Empowerment</h3>
                    <p className="text-gray-700">
                      We believe in empowering everyone with the tools and insights needed to achieve 
                      financial wellness. Our platform provides comprehensive financial management and 
                      AI-powered guidance to help you reach your goals.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-semibold text-gray-800 mt-10 mb-4">Our Commitments</h2>
              
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-8">
                <li>
                  <strong>To you:</strong> We commit to creating tools that help you take control of your 
                  finances, whether you're managing money solo or with a partner. Your financial wellness 
                  is our priority.
                </li>
                <li>
                  <strong>To transparency:</strong> We commit to clear, straightforward pricing without 
                  hidden fees or selling your financial data. Your privacy and security are paramount.
                </li>
                <li>
                  <strong>To innovation:</strong> We commit to continuously improving our AI and machine 
                  learning capabilities to provide better insights, recommendations, and financial guidance.
                </li>
                <li>
                  <strong>To inclusivity:</strong> We commit to designing for everyone—individuals, couples, 
                  and partners—with features that adapt to your unique financial situation and goals.
                </li>
              </ul>

              <div className="flex justify-center mt-10">
                <Link 
                  to="/register" 
                  className="px-8 py-3 bg-gradient-to-r from-[#6C5DD3] to-[#8247E5] text-white font-medium rounded-xl hover:opacity-90 transition duration-200"
                >
                  Join Our Mission
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mission; 